import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Play, Pause, Share2, Heart, Music2, Clock, Zap } from 'lucide-react';
import { API_BASE as API } from '../lib/apiBase';


export default function TrackPage() {
  const { id } = useParams();
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [copied, setCopied] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/producer/track/${id}`)
      .then(r => r.json())
      .then(data => { if (data.id) setTrack(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const togglePlay = () => {
    if (!audioRef.current || !track?.audio_url) return;
    if (playing) { audioRef.current.pause(); }
    else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: `${track.title} - ${track.artist_name}`, url });
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
      <div className="animate-pulse text-[#71717A]">Cargando...</div>
    </div>
  );

  if (!track) return (
    <div className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center gap-4">
      <Music2 size={48} className="text-[#27272A]" />
      <p className="text-[#71717A]">Track no encontrado</p>
      <Link to="/" className="text-[#F5C842] hover:underline text-sm">Ir a PulseMusic</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center p-4" data-testid="track-page">
      {track.audio_url && <audio ref={audioRef} src={track.audio_url} onEnded={() => setPlaying(false)} />}

      <div className="w-full max-w-md">
        {/* Cover */}
        <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#18181B] mb-6">
          {track.cover ? (
            <img src={track.cover} alt={track.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#F5C842]/20 to-[#7C3AED]/20">
              <Music2 size={80} className="text-[#27272A]" />
            </div>
          )}
          {/* Play overlay */}
          <button
            onClick={togglePlay}
            data-testid="track-play-btn"
            className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
          >
            {playing ? (
              <Pause size={64} className="text-white drop-shadow-lg" fill="white" />
            ) : (
              <Play size={64} className="text-white drop-shadow-lg ml-2" fill="white" />
            )}
          </button>
        </div>

        {/* Info */}
        <h1 className="text-2xl font-bold text-white mb-1" data-testid="track-title">{track.title}</h1>
        <p className="text-[#A1A1AA] mb-4" data-testid="track-artist">{track.artist_name}</p>

        {/* Meta chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          {track.genre && (
            <span className="px-2.5 py-1 bg-[#F5C842]/10 text-[#F5C842] rounded-full text-xs">{track.genre}</span>
          )}
          {track.mood && (
            <span className="px-2.5 py-1 bg-[#7C3AED]/10 text-[#7C3AED] rounded-full text-xs">{track.mood}</span>
          )}
          {track.bpm > 0 && (
            <span className="px-2.5 py-1 bg-[#18181B] text-[#71717A] rounded-full text-xs flex items-center gap-1">
              <Zap size={10} /> {track.bpm} BPM
            </span>
          )}
          {track.duration > 0 && (
            <span className="px-2.5 py-1 bg-[#18181B] text-[#71717A] rounded-full text-xs flex items-center gap-1">
              <Clock size={10} /> {Math.floor(track.duration / 60)}:{String(Math.round(track.duration % 60)).padStart(2, '0')}
            </span>
          )}
        </div>

        {track.description && (
          <p className="text-sm text-[#A1A1AA] mb-6 leading-relaxed">{track.description}</p>
        )}

        {/* Actions */}
        <div className="flex gap-3 mb-8">
          <button onClick={handleShare} data-testid="track-share-btn"
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#F5C842] text-black rounded-xl font-medium hover:bg-[#F5C842]/90 transition-colors">
            <Share2 size={16} />
            {copied ? 'Link copiado!' : 'Compartir'}
          </button>
          <button className="flex items-center justify-center gap-2 px-5 py-3 bg-[#18181B] text-white rounded-xl hover:bg-[#27272A] transition-colors">
            <Heart size={16} />
            {track.likes || 0}
          </button>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-center gap-4 text-xs text-[#52525B] mb-8">
          <span>{track.plays || 0} reproducciones</span>
          {track.sonic_palette && <span>{track.sonic_palette}</span>}
        </div>

        {/* CTA */}
        <div className="text-center border-t border-[#18181B] pt-6">
          <p className="text-[#71717A] text-sm mb-3">Creado con PULSE Producer</p>
          <Link to="/producer" data-testid="track-cta-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F5C842] to-[#F59E0B] text-black rounded-xl font-medium hover:opacity-90 transition-opacity">
            <Music2 size={16} />
            Crea tu propia musica con IA
          </Link>
        </div>
      </div>
    </div>
  );
}
